import fetchWithTimeout from './fetchWithTimeout';

export type FetchHeaders = { [key: string]: string };

const FETCH_ERROR_TIMEOUT = 'FETCH_TIMEOUT';
const DEFAULT_TIMEOUT = 5000;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000;

const delay = (t: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, t);
  });

type FetchWithTimeoutRetry = {
  url: string;
  method?: string;
  body: string;
  timeout?: number;
  maxRetries?: number;
  retriesDone?: number;
  retryDelay?: number;
  headers?: FetchHeaders;
  // Called with each attempt's raw response before its body is parsed (MPH-1410: the x-tsx-mpt-renew signal).
  onResponse?: (response: Response) => void;
};

export const fetchWithRetryTimeout = async <Res>({
  url,
  method = 'POST',
  body,
  timeout = DEFAULT_TIMEOUT,
  maxRetries = DEFAULT_MAX_RETRIES,
  retriesDone = 0,
  retryDelay = DEFAULT_RETRY_DELAY,
  headers = {},
  onResponse,
}: FetchWithTimeoutRetry): Promise<Res> => {
  const response = await fetchWithTimeout(
    url,
    {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body,
    },
    timeout,
    onResponse
  ).catch(async () => {
    if (retriesDone < maxRetries) {
      await delay(retryDelay);
      const retryResponse = await fetchWithRetryTimeout<Res>({
        url,
        method,
        body,
        timeout,
        maxRetries,
        retriesDone: retriesDone + 1,
        retryDelay,
        headers,
        onResponse,
      });
      return retryResponse;
    }
    throw new Error(FETCH_ERROR_TIMEOUT);
  });

  return response;
};
